/* The Storefront API, as much of it as this shop actually uses.
 *
 * Two things live in Shopify and nowhere else: what a garment costs and
 * whether it can still be sold, and the cart that turns into an order. The
 * photography, the names and the copy stay in `@/config/products.js` — the
 * shop's own products carry no images at all, and the cut-outs the floor is
 * built from could not come from there anyway.
 *
 * The token is a public Storefront token: it is meant to ship in the bundle,
 * it can only read published products and drive a cart, and it is scoped
 * without inventory access, so quantityAvailable is unreadable here and
 * availableForSale is the only stock signal there is. */

const DOMAIN = process.env.VUE_APP_SHOPIFY_STORE_DOMAIN;
const TOKEN = process.env.VUE_APP_SHOPIFY_STOREFRONT_ACCESS_TOKEN;

const API_VERSION = '2025-07';

/* A build without credentials is a normal thing — a fork, a preview, a local
   checkout without the .env. The pages check this and stay readable rather
   than filling up with errors nobody can act on. */
export const isConfigured = Boolean(DOMAIN && TOKEN);

export class ShopifyError extends Error {
  constructor(message) {
    super(message);
    this.name = 'ShopifyError';
  }
}

async function storefront(query, variables = {}) {
  if (!isConfigured) throw new ShopifyError('Shopify ist nicht konfiguriert.');

  const response = await fetch(`https://${DOMAIN}/api/${API_VERSION}/graphql.json`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': TOKEN,
    },
    body: JSON.stringify({ query, variables }),
  });

  if (!response.ok) {
    throw new ShopifyError(`Shopify antwortete mit ${response.status}.`);
  }

  const payload = await response.json();

  /* GraphQL answers 200 with a partial result and an errors list. A field we
     have no scope for is one of those, so the errors only count as a failure
     when there is no data to go with them. */
  if (!payload.data) {
    const detail = (payload.errors || []).map((error) => error.message).join(', ');
    throw new ShopifyError(detail || 'Shopify lieferte keine Daten.');
  }

  return payload.data;
}

/* Everything the cart needs to draw itself and to hand over to the checkout.
   The line's own product handle is what ties it back to the local photograph
   and name. */
const CART_FIELDS = `
  id
  checkoutUrl
  totalQuantity
  cost {
    subtotalAmount { amount currencyCode }
    totalAmount { amount currencyCode }
  }
  lines(first: 50) {
    edges {
      node {
        id
        quantity
        cost { totalAmount { amount currencyCode } }
        merchandise {
          ... on ProductVariant {
            id
            title
            availableForSale
            price { amount currencyCode }
            product { handle }
          }
        }
      }
    }
  }
`;

function flattenCart(cart) {
  if (!cart) return null;
  return {
    id: cart.id,
    checkoutUrl: cart.checkoutUrl,
    totalQuantity: cart.totalQuantity,
    subtotal: cart.cost.subtotalAmount,
    lines: cart.lines.edges.map(({ node }) => ({
      id: node.id,
      quantity: node.quantity,
      lineTotal: node.cost.totalAmount,
      variantId: node.merchandise.id,
      size: node.merchandise.title,
      available: node.merchandise.availableForSale,
      price: node.merchandise.price,
      handle: node.merchandise.product.handle,
    })),
  };
}

/* One request for the whole catalogue rather than one per product: the shop
   holds five garments, and aliasing them into a single query keeps the floor
   from firing five round trips before it can show a price. */
export async function fetchProducts(handles) {
  const fields = `
    handle
    availableForSale
    variants(first: 24) {
      edges {
        node {
          id
          title
          availableForSale
          price { amount currencyCode }
        }
      }
    }
  `;

  const query = `query catalogue(${handles.map((_, i) => `$h${i}: String!`).join(', ')}) {
    ${handles.map((_, i) => `p${i}: product(handle: $h${i}) { ${fields} }`).join('\n')}
  }`;

  const variables = Object.fromEntries(handles.map((handle, i) => [`h${i}`, handle]));
  const data = await storefront(query, variables);

  // A handle that no longer resolves comes back null; it is dropped here so
  // the catalogue simply has no entry for it rather than an empty one.
  return handles
    .map((handle, i) => data[`p${i}`])
    .filter(Boolean)
    .map((product) => ({
      handle: product.handle,
      availableForSale: product.availableForSale,
      variants: product.variants.edges.map(({ node }) => ({
        id: node.id,
        size: node.title,
        available: node.availableForSale,
        price: node.price,
      })),
    }));
}

/* Shopify's cart mutations all answer in the same shape: the cart, plus a list
   of errors that arrive with a 200. Raising them here means every caller can
   treat a returned cart as a cart. */
async function mutateCart(query, variables, field) {
  const data = await storefront(query, variables);
  const result = data[field];
  const failure = (result.userErrors || [])[0];
  if (failure) throw new ShopifyError(failure.message);
  return flattenCart(result.cart);
}

export function createCart(lines = []) {
  return mutateCart(`
    mutation createCart($lines: [CartLineInput!]) {
      cartCreate(input: { lines: $lines }) {
        cart { ${CART_FIELDS} }
        userErrors { field message }
      }
    }
  `, { lines }, 'cartCreate');
}

export function addCartLines(cartId, lines) {
  return mutateCart(`
    mutation addLines($cartId: ID!, $lines: [CartLineInput!]!) {
      cartLinesAdd(cartId: $cartId, lines: $lines) {
        cart { ${CART_FIELDS} }
        userErrors { field message }
      }
    }
  `, { cartId, lines }, 'cartLinesAdd');
}

export function updateCartLines(cartId, lines) {
  return mutateCart(`
    mutation updateLines($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
      cartLinesUpdate(cartId: $cartId, lines: $lines) {
        cart { ${CART_FIELDS} }
        userErrors { field message }
      }
    }
  `, { cartId, lines }, 'cartLinesUpdate');
}

export function removeCartLines(cartId, lineIds) {
  return mutateCart(`
    mutation removeLines($cartId: ID!, $lineIds: [ID!]!) {
      cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
        cart { ${CART_FIELDS} }
        userErrors { field message }
      }
    }
  `, { cartId, lineIds }, 'cartLinesRemove');
}

/* Null rather than an error when the id is stale: a cart Shopify has already
   swept up is not a failure, it is an empty cart. */
export async function fetchCart(cartId) {
  const data = await storefront(`
    query cart($cartId: ID!) {
      cart(id: $cartId) { ${CART_FIELDS} }
    }
  `, { cartId });

  return flattenCart(data.cart);
}
